import { ref } from 'vue'
import { useCalendarComputed } from './useCalendarComputed.js'
import { useCalendarFormatting } from './useCalendarFormatting.js'

/** `YYYY-MM-DD` lu comme un jour local (`new Date('2026-09-01')` est minuit UTC). */
function parseLocalDay(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  return new Date(value)
}

/**
 * Le mois où s'ouvre le calendrier : celui du premier jour coché encore
 * sélectionnable, pas celui du premier élément de la liste (non triée, et
 * parfois un jour passé). Sans jour à venir, le plus ancien ; sans jour, aujourd'hui.
 */
function getInitialDate(dates, minSelectableDate) {
  const days = (dates || [])
    .map(parseLocalDay)
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => a - b)
  return days.find((d) => d >= minSelectableDate) || days[0] || new Date()
}

/**
 * Composable pour gérer un calendrier interactif
 * Limite: 80 lignes max
 * 
 * @param {Array} selectedDates - Tableau de dates sélectionnées (format YYYY-MM-DD)
 * @param {Date} minDate - Date minimum sélectionnable (optionnel)
 * @returns {Object} Fonctions et états du calendrier
 */
export function useCalendar(selectedDates = ref([]), minDate = null) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const minSelectableDate = minDate || today
  const initialDate = getInitialDate(selectedDates.value, minSelectableDate)
  const currentMonth = ref(initialDate.getMonth())
  const currentYear = ref(initialDate.getFullYear())
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

  const {
    currentMonthYear,
    calendarDays,
    isPreviousMonthDisabled,
    sortedSelectedDates
  } = useCalendarComputed(currentMonth, currentYear, selectedDates, minSelectableDate)

  const { formatDateForStorage, formatDateForDisplay } = useCalendarFormatting()

  const isDateSelected = (date) => {
    const dateStr = formatDateForStorage(date)
    return selectedDates.value.includes(dateStr)
  }

  const isDateDisabled = (date) => {
    return date < minSelectableDate
  }

  const toggleDate = (date) => {
    if (isDateDisabled(date)) return

    const dateStr = formatDateForStorage(date)
    const index = selectedDates.value.indexOf(dateStr)

    if (index > -1) {
      // Créer un nouveau tableau pour déclencher le setter du computed
      selectedDates.value = selectedDates.value.filter(d => d !== dateStr)
    } else {
      // Créer un nouveau tableau pour déclencher le setter du computed
      selectedDates.value = [...selectedDates.value, dateStr]
    }
  }

  const removeDate = (dateStr) => {
    // Créer un nouveau tableau pour déclencher le setter du computed
    selectedDates.value = selectedDates.value.filter(d => d !== dateStr)
  }

  const previousMonth = () => {
    if (currentMonth.value === 0) {
      currentMonth.value = 11
      currentYear.value--
    } else {
      currentMonth.value--
    }
  }

  const nextMonth = () => {
    if (currentMonth.value === 11) {
      currentMonth.value = 0
      currentYear.value++
    } else {
      currentMonth.value++
    }
  }

  return {
    currentMonth,
    currentYear,
    weekDays,
    currentMonthYear,
    calendarDays,
    formatDateForStorage,
    formatDateForDisplay,
    isDateSelected,
    isDateDisabled,
    toggleDate,
    removeDate,
    previousMonth,
    nextMonth,
    isPreviousMonthDisabled,
    sortedSelectedDates
  }
}

